<template>
  <v-dialog :value="value" max-width="960" persistent @input="onDialogInput">
    <v-card>
      <v-card-title class="headline">
        Gerar descricao com IA
      </v-card-title>

      <v-card-text>
        <v-row dense>
          <v-col cols="12">
            <strong>Produto:</strong> {{ productName || "-" }}
          </v-col>
          <v-col cols="12">
            <v-textarea
              v-model="additionalContext"
              label="Contexto adicional"
              outlined
              rows="3"
              :disabled="isLoading"
              :counter="ADDITIONAL_CONTEXT_MAX_LENGTH"
              :maxlength="ADDITIONAL_CONTEXT_MAX_LENGTH"
              hint="Informacoes extras para orientar a geracao (opcional)"
              persistent-hint
            />
          </v-col>
          <v-col cols="12">
            <v-alert
              v-if="errorState !== 'NONE'"
              type="error"
              dense
              text
              data-test="error-alert"
            >
              {{ errorMessage }}
            </v-alert>
            <div
              class="product-description-ai-modal__preview"
              :class="{
                'product-description-ai-modal__preview--loading': isLoading
              }"
            >
              <v-progress-linear
                v-if="isLoading"
                indeterminate
                color="primary"
                class="mb-3"
              />
              <div
                v-else-if="generatedHtmlSafe"
                data-test="preview"
                v-html="generatedHtmlSafe"
              />
              <span v-else class="product-description-ai-modal__placeholder">
                A visualizacao da descricao gerada aparecera aqui.
              </span>
            </div>
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider />

      <v-card-actions class="pa-4">
        <v-spacer />
        <FormButton
          text="Cancelar"
          type="perigo"
          data-test="cancel"
          :disabled="isLoading"
          @click="onCancel"
        />
        <FormButton
          v-if="canRetry"
          text="Tentar novamente"
          type="auxiliar"
          data-test="retry"
          :disabled="isLoading || !canGenerate"
          @click="onRetry"
        />
        <FormButton
          text="Regenerar"
          type="auxiliar"
          data-test="regenerate"
          :disabled="isLoading || !hasGeneratedContent"
          @click="onRegenerate"
        />
        <FormButton
          text="Gerar"
          type="primario"
          data-test="generate"
          :disabled="isLoading || !canGenerate"
          :loading="isLoading"
          @click="onGenerate"
        />
        <FormButton
          text="Aceitar"
          type="submissao"
          data-test="accept"
          :disabled="isLoading || !hasGeneratedContent"
          @click="onAccept"
        />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import DOMPurify from "dompurify";
import FormButton from "@/components/form/FormButton";
import { PRODUTO_GENERATE_DESCRIPTION } from "@/store/actions/produto";

const ADDITIONAL_CONTEXT_MAX_LENGTH = 2000;

const DEFAULT_ERROR_MESSAGES = {
  TIMEOUT: "A geracao demorou mais que o esperado. Tente novamente.",
  UNAVAILABLE: "Servico de IA indisponivel no momento. Tente em instantes.",
  RATE_LIMIT:
    "Limite de 10 produtos por minuto atingido para este fornecedor. Aguarde e tente novamente.",
  VALIDATION: "Nao foi possivel gerar a descricao com os dados informados.",
  UNKNOWN: "Ocorreu um erro inesperado ao gerar a descricao."
};

const RETRYABLE_ERROR_STATES = [
  "TIMEOUT",
  "UNAVAILABLE",
  "RATE_LIMIT",
  "UNKNOWN"
];

export default {
  name: "ProductDescriptionAiModal",

  components: {
    FormButton
  },

  props: {
    value: {
      type: Boolean,
      default: false
    },
    productName: {
      type: String,
      default: ""
    },
    shortDescription: {
      type: String,
      default: ""
    },
    currentLongDescription: {
      type: String,
      default: ""
    },
    additionalContextDefault: {
      type: String,
      default: ""
    },
    additionalNames: {
      type: Array,
      default: () => []
    },
    idfornecedor: {
      type: [String, Number],
      default: ""
    }
  },

  data() {
    return {
      ADDITIONAL_CONTEXT_MAX_LENGTH,
      additionalContext: "",
      isLoading: false,
      errorState: "NONE",
      errorMessage: "",
      generatedHtmlRaw: "",
      generatedHtmlSafe: ""
    };
  },

  computed: {
    canGenerate() {
      return !!(this.productName && this.productName.trim());
    },
    hasGeneratedContent() {
      return !!(this.generatedHtmlSafe && this.generatedHtmlSafe.trim());
    },
    canRetry() {
      return RETRYABLE_ERROR_STATES.includes(this.errorState);
    }
  },

  watch: {
    value(isOpen) {
      if (isOpen) {
        if (!this.additionalContext && this.additionalContextDefault) {
          this.additionalContext = this.additionalContextDefault;
        }
        return;
      }
      this.resetGenerationState();
    }
  },

  methods: {
    buildPayload() {
      const additionalNames = (this.additionalNames || [])
        .map(name => (name || "").trim())
        .filter(Boolean);

      return {
        productName: (this.productName || "").trim(),
        shortDescription: (this.shortDescription || "").trim(),
        additionalNames,
        additionalContext: (this.additionalContext || "").trim()
      };
    },

    normalizeErrorCode(code) {
      if (code === "VALIDATION" || code === "VALIDATION_ERROR") {
        return "VALIDATION";
      }
      return code || "UNKNOWN";
    },

    clearError() {
      this.errorState = "NONE";
      this.errorMessage = "";
    },

    setError(error) {
      const code = this.normalizeErrorCode(error.code);
      this.errorState = code;
      this.errorMessage =
        error.message ||
        DEFAULT_ERROR_MESSAGES[code] ||
        DEFAULT_ERROR_MESSAGES.UNKNOWN;
      this.generatedHtmlRaw = "";
      this.generatedHtmlSafe = "";
    },

    resetGenerationState() {
      this.isLoading = false;
      this.clearError();
      this.generatedHtmlRaw = "";
      this.generatedHtmlSafe = "";
    },

    sanitizeHtml(rawHtml) {
      return DOMPurify.sanitize(rawHtml || "", {
        USE_PROFILES: { html: true }
      });
    },

    applyGeneratedContent(rawHtml) {
      if (!rawHtml || !String(rawHtml).trim()) {
        this.setError({
          code: "VALIDATION",
          message: DEFAULT_ERROR_MESSAGES.VALIDATION
        });
        return false;
      }

      this.generatedHtmlRaw = rawHtml;
      this.generatedHtmlSafe = this.sanitizeHtml(this.generatedHtmlRaw);

      if (!this.generatedHtmlSafe.trim()) {
        this.setError({
          code: "VALIDATION",
          message: DEFAULT_ERROR_MESSAGES.VALIDATION
        });
        return false;
      }

      return true;
    },

    async dispatchGenerate() {
      return this.$store.dispatch(PRODUTO_GENERATE_DESCRIPTION, {
        idfornecedor: this.idfornecedor,
        payload: this.buildPayload()
      });
    },

    async onGenerate() {
      if (this.isLoading || !this.canGenerate) {
        return;
      }

      this.isLoading = true;
      this.clearError();

      try {
        const result = await this.dispatchGenerate();
        if (!result || !result.descriptionHtml) {
          this.setError({
            code: "VALIDATION",
            message: DEFAULT_ERROR_MESSAGES.VALIDATION
          });
          return;
        }
        this.applyGeneratedContent(result.descriptionHtml);
      } catch (error) {
        this.setError(error);
      } finally {
        this.isLoading = false;
      }
    },

    onRetry() {
      return this.onGenerate();
    },

    onDialogInput(isOpen) {
      if (!isOpen && this.isLoading) {
        return;
      }
      this.$emit("input", isOpen);
    },

    onCancel() {
      if (this.isLoading) {
        return;
      }
      this.$emit("cancel");
      this.$emit("input", false);
    },

    onAccept() {
      if (this.isLoading || !this.hasGeneratedContent) {
        return;
      }

      this.$emit("accept", {
        descriptionHtml: this.generatedHtmlSafe,
        additionalContext: this.additionalContext,
        source: "ai"
      });
      this.$emit("input", false);
    },

    async onRegenerate() {
      if (this.isLoading) {
        return;
      }

      this.$emit("regenerate", {
        additionalContext: this.additionalContext
      });
      return this.onGenerate();
    }
  }
};
</script>

<style scoped>
.product-description-ai-modal__preview {
  min-height: 140px;
  border: 1px dashed #d0d4db;
  border-radius: 4px;
  padding: 12px;
  color: #606b7b;
  background-color: #fafbfc;
}

.product-description-ai-modal__preview--loading {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.product-description-ai-modal__placeholder {
  color: #606b7b;
}
</style>
